import { Badge, Card, CardContent, CardHeader, CardTitle } from "@/components/ui";
import { useEventReferrals } from "@/api/hooks/useProof";
import { STAGE_LABELS } from "@/api/types/inquiry";
import { formatShortDate } from "./eventDate";

interface EventReferralsCardProps {
  eventId: string;
}

/**
 * Read-only: inquiries whose `referral_event_id` points back at this event
 * (GET /v2/events/:id/referrals) — closes the loop on "deal #1 becomes deal
 * #2." No mutations happen here; referral linkage is set on the inquiry
 * side (Phase 1/7 territory), not from the event detail page.
 */
export function EventReferralsCard({ eventId }: EventReferralsCardProps) {
  const { data: referrals } = useEventReferrals(eventId);
  const list = referrals ?? [];

  return (
    <Card data-testid="referrals-card">
      <CardHeader>
        <CardTitle>Referrals from this event</CardTitle>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="text-sm text-text-muted" data-testid="referrals-empty">
            No referrals linked to this event yet.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="referral-list">
            {list.map((inquiry) => (
              <li
                key={inquiry.id}
                className="flex flex-col gap-1 py-2 sm:flex-row sm:items-center sm:justify-between"
                data-inquiry-id={inquiry.id}
              >
                <div>
                  <p className="text-sm font-medium text-text-primary">{inquiry.contact_name}</p>
                  {inquiry.event_date && (
                    <p className="text-xs text-text-muted">{formatShortDate(inquiry.event_date)}</p>
                  )}
                </div>
                <Badge variant="secondary">{STAGE_LABELS[inquiry.stage]}</Badge>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
